import React, { FC } from 'react';
import { Button } from 'antd';
import { Image } from 'common/components';
import { getPriceToDisplay, priceFormatter } from 'common/helpers';
import { IGoodsShort } from 'common/models';
import { AuthHooks, GoodHooks, UserHooks } from 'common/hooks';
import { Cart } from 'common/svg';
import { useAppSelector } from 'app/store/store.hooks';
import { GoodDiscount } from 'entities/Goods/components';
import { selectCurrentUser } from 'entities/User/User.slice';
import { selectXBasketId } from 'entities/Auth/Auth.slice';

interface IComponentProps {
  analog: IGoodsShort;
  loading: boolean;
  onAddToBasketClick: (id: number, count: number) => void;
  onNavigateToBasketClick: () => void;
}

export const GoodAnalogCard: FC<IComponentProps> = ({ analog, loading, onAddToBasketClick, onNavigateToBasketClick }) => {
  // auth
  const xBasketId = useAppSelector(selectXBasketId);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const { isSeller } = AuthHooks.useIsSeller();
  const { isSelfRegistered } = UserHooks.useIsSelfRegistered();
  const { getGoodInBasket } = GoodHooks.useGoodInBasket();

  const isCustomer = !isSeller || !!xBasketId;
  const priceToDisplay = getPriceToDisplay(analog.price, analog.priceWithTaxes, currentUser?.account);
  const formattedPrice = priceFormatter(priceToDisplay);
  const formattedPriceWithTaxes = priceFormatter(analog.priceWithTaxes);
  const goodInBasket = getGoodInBasket(analog.id);

  const addGoodToBasket = (e: React.MouseEvent, id: number): void => {
    e.stopPropagation();
    e.preventDefault();
    onAddToBasketClick(id, 1);
  };

  const handleNavigateToBasket = (e: React.MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    onNavigateToBasketClick();
  };

  return (
    <a
      href={`/good/${analog.id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex direction-column justify-between text-decoration-none"
    >
      <div className="relative">
        {(analog.discountValue ?? 0) > 0 && (
          <div className="absolute" style={{ top: 0, right: 0, zIndex: 1 }}>
            <GoodDiscount discount={analog.discountValue} min />
          </div>
        )}

        <div className="mb-3">
          <Image src={analog.images?.[0]} size={168} />
        </div>

        <div className="mb-3 flex align-center wrap">
          <div className="ff-medium fs-l color-black ellipsis" title={formattedPrice}>
            {formattedPrice}
          </div>

          {!isSelfRegistered && (
            <span className="mt-2 ff-regular fs-m color-grey ellipsis" title={formattedPriceWithTaxes}>
              {`с НДС ${formattedPriceWithTaxes}`}
            </span>
          )}
        </div>

        <div className="mb-5 ff-regular fs-s color-black ellipsis-3 word-break-word">{analog.name}</div>
      </div>

      {isCustomer && analog.isAvailableForCustomer && (
        <div>
          {goodInBasket ? (
            <Button className="button-sm success w-full" onClick={handleNavigateToBasket}>
              В корзине
            </Button>
          ) : (
            <Button
              className="button-sm primary w-full"
              icon={<Cart className="icon-white" />}
              loading={loading}
              onClick={(e) => addGoodToBasket(e, analog.id)}
            >
              В корзину
            </Button>
          )}
        </div>
      )}
    </a>
  );
};
